import { Capacitor, registerPlugin } from '@capacitor/core';
import { speak as sharedSpeak } from '@caro/shared/utils';
import { logEvent } from '../utils/debugLog';

/**
 * Wat de native TTS-engine terugmeldde. Tot 2026-09-24 kwam hier niets terug en kon een
 * tablet zonder werkende engine "voorgelezen" rapporteren terwijl hij stil bleef.
 */
export interface TtsSpeakResult {
  ready?: boolean;
  spoken?: boolean;
  speakResult?: number;
  langResult?: number;
  engine?: string;
  reason?: string;
}

/**
 * Ruwe native momentopname voor de batterijmeter. Alles optioneel: een oudere APK kent
 * `getPowerStats` niet, en een fabrikant kan elk veld weglaten. Normaliseren gebeurt in
 * `utils/powerStats.ts`, niet hier.
 */
export interface NativePowerStats {
  capacityPct?: number;
  levelPct?: number;
  currentNowRaw?: number;
  currentAvgRaw?: number;
  chargeCounterUah?: number;
  voltageMv?: number;
  temperatureDeciC?: number;
  plugged?: 'ac' | 'usb' | 'wireless' | 'none' | 'other';
  status?: 'charging' | 'discharging' | 'full' | 'not_charging' | 'unknown';
  systemBrightness?: number;
  autoBrightness?: boolean;
  windowBrightness?: number;
  keepScreenOn?: boolean;
  interactive?: boolean;
  powerSaveMode?: boolean;
  ignoringBatteryOptimizations?: boolean;
  versionName?: string;
  versionCode?: number;
  firstInstallTime?: number;
  lastUpdateTime?: number;
  installer?: string;
  androidRelease?: string;
  model?: string;
}

interface PermissionStates {
  microphone: string;
  camera: string;
  location: string;
  notifications: string;
}

interface KeepAlivePlugin {
  startService(): Promise<void>;
  stopService(): Promise<void>;
  wakeScreen(): Promise<void>;
  clearWake(): Promise<void>;
  openAppSettings(): Promise<void>;
  bringToFront(): Promise<void>;
  getDeviceMac(): Promise<{ mac: string }>;
  getDeviceIds(): Promise<{ androidId: string; managedUdid: string }>;
  getPowerStats(): Promise<NativePowerStats>;
  setWindowBrightness(options: { value: number; durationMs?: number }): Promise<void>;
  canDrawOverlays(): Promise<{ granted: boolean }>;
  requestOverlayPermission(): Promise<void>;
  checkPermissions(): Promise<PermissionStates>;
  requestPermissions(): Promise<PermissionStates>;
  speak(options: { text: string; lang?: string }): Promise<TtsSpeakResult | void>;
  stopTts(): Promise<void>;
}

const KeepAlive = registerPlugin<KeepAlivePlugin>('KeepAlive');
const isNative = Capacitor.isNativePlatform();
let powerStatsWarned = false;

export const KeepAliveBridge = {
  async startService() {
    if (!isNative) return;
    try {
      await KeepAlive.startService();
    } catch (e) {
      console.warn('[KeepAlive] startService failed:', e);
    }
  },

  async stopService() {
    if (!isNative) return;
    try {
      await KeepAlive.stopService();
    } catch (e) {
      console.warn('[KeepAlive] stopService failed:', e);
    }
  },

  async wakeScreen() {
    if (!isNative) return;
    try {
      await KeepAlive.wakeScreen();
    } catch (e) {
      console.warn('[KeepAlive] wakeScreen failed:', e);
    }
  },

  async clearWake() {
    if (!isNative) return;
    try {
      await KeepAlive.clearWake();
    } catch (e) {
      console.warn('[KeepAlive] clearWake failed:', e);
    }
  },

  async openAppSettings() {
    if (!isNative) return;
    try {
      await KeepAlive.openAppSettings();
    } catch (e) {
      console.warn('[KeepAlive] openAppSettings failed:', e);
    }
  },

  async bringToFront() {
    if (!isNative) return;
    try {
      await KeepAlive.bringToFront();
    } catch (e) {
      console.warn('[KeepAlive] bringToFront failed:', e);
    }
  },

  async getDeviceMac(): Promise<string> {
    if (!isNative) return 'web-browser';
    try {
      const result = await KeepAlive.getDeviceMac();
      return result.mac;
    } catch (e) {
      console.warn('[KeepAlive] getDeviceMac failed:', e);
      return 'unknown';
    }
  },

  // Join-key diagnostics: this APK's own ANDROID_ID vs the MDM-pushed managed
  // config value. Used to decide the hands-free provisioning path.
  async getDeviceIds(): Promise<{ androidId: string; managedUdid: string }> {
    if (!isNative) return { androidId: 'web', managedUdid: '' };
    try {
      return await KeepAlive.getDeviceIds();
    } catch (e) {
      console.warn('[KeepAlive] getDeviceIds failed:', e);
      return { androidId: 'error', managedUdid: '' };
    }
  },

  /**
   * Batterij/scherm/APK-versie. `null` in de browser of op een APK van vóór 1.0.4
   * (versionCode 5) — die kent de methode niet, en dat is zelf informatie.
   */
  async getPowerStats(): Promise<NativePowerStats | null> {
    if (!isNative) return null;
    try {
      return await KeepAlive.getPowerStats();
    } catch (e) {
      // "not implemented" = oudere APK; iets anders = een fout in de native methode.
      // Eén keer per sessie in het diagnoselogboek, anders elke 15 s.
      if (!powerStatsWarned) {
        powerStatsWarned = true;
        logEvent('power.native_unavailable', { error: e instanceof Error ? e.message : String(e) });
      }
      return null;
    }
  },

  /**
   * Echte schermverlichting van dit venster: `value` 0..1, vloeiend over `durationMs`;
   * een negatieve waarde geeft de helderheid terug aan de systeeminstelling. Doet niets
   * in de browser of op een APK < 1.0.5 (versionCode 6) — die dimmen alleen met CSS.
   */
  async setWindowBrightness(value: number, durationMs = 0): Promise<boolean> {
    if (!isNative) return false;
    try {
      await KeepAlive.setWindowBrightness({ value, durationMs });
      return true;
    } catch {
      /* oudere APK: geen native helderheid */
      return false;
    }
  },

  async canDrawOverlays(): Promise<boolean> {
    if (!isNative) return true;
    try {
      const result = await KeepAlive.canDrawOverlays();
      return result.granted;
    } catch (e) {
      console.warn('[KeepAlive] canDrawOverlays failed:', e);
      return true;
    }
  },

  async requestOverlayPermission() {
    if (!isNative) return;
    try {
      await KeepAlive.requestOverlayPermission();
    } catch (e) {
      console.warn('[KeepAlive] requestOverlayPermission failed:', e);
    }
  },

  /**
   * Speak text using Android's native TTS engine.
   * Falls back to Web Speech API on non-native platforms.
   */
  async speak(text: string, lang: string = 'nl'): Promise<void> {
    if (!isNative) {
      // Fallback to shared Web Speech API utility on browser / desktop.
      // Routes through `@caro/shared/utils.speak` so there is exactly one
      // TTS implementation in the resident-app (T3.2 / F3-min.5).
      try {
        await sharedSpeak(text, { lang });
      } catch (e) {
        console.warn('[KeepAlive] Web Speech fallback failed:', e);
      }
      return;
    }
    try {
      const result = (await KeepAlive.speak({ text, lang })) as TtsSpeakResult | void;
      // Op de diagnose-overlay, zodat "ik hoor geen stem" een meetbare oorzaak krijgt:
      // geen engine (`ready: false`), taal niet geïnstalleerd (`langResult` < 0) of een
      // geweigerde `speak()` (`spoken: false`). Een leeg resultaat is een oudere APK.
      if (result && result.spoken === false) {
        logEvent('tts:silent', { lang, ...result });
        console.warn('[KeepAlive] TTS did not speak:', result);
      } else {
        logEvent('tts:spoken', { lang, ...(result || { legacy: true }) });
      }
    } catch (e) {
      logEvent('tts:error', { lang, error: e instanceof Error ? e.message : String(e) });
      console.warn('[KeepAlive] speak failed:', e);
    }
  },

  async stopTts(): Promise<void> {
    if (!isNative) {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
      return;
    }
    try {
      await KeepAlive.stopTts();
    } catch (e) {
      console.warn('[KeepAlive] stopTts failed:', e);
    }
  },

  /**
   * Check all Android permission states natively.
   * Returns { microphone, camera, location, notifications } with values
   * "granted", "prompt", or "denied".
   */
  async checkPermissions(): Promise<PermissionStates | null> {
    if (!isNative) return null;
    try {
      return await KeepAlive.checkPermissions();
    } catch (e) {
      console.warn('[KeepAlive] checkPermissions failed:', e);
      return null;
    }
  },

  /**
   * Request all app permissions via Android's native permission dialog.
   * Returns the updated permission states after the user responds.
   */
  async requestPermissions(): Promise<PermissionStates | null> {
    if (!isNative) return null;
    try {
      return await KeepAlive.requestPermissions();
    } catch (e) {
      console.warn('[KeepAlive] requestPermissions failed:', e);
      return null;
    }
  },
};
